import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { updateGroupProjectLink } from '../../../API/apiService';
import './style.scss';

const MAX_PROJECT_LINK_LENGTH = 255;

function normalizeProjectLink(value) {
  const rawValue = String(value || '').trim();

  if (!rawValue) {
    return '';
  }

  return /^https?:\/\//i.test(rawValue) ? rawValue : `https://${rawValue}`;
}

function isValidProjectLink(value) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname);
  } catch (error) {
    return false;
  }
}

function getInitialProjectLink(group) {
  const description = String(group?.description || '').trim();
  return /^https?:\/\//i.test(description) ? description : '';
}

function ProjectLinkModal({ group, onClose, onSaved, saveProjectLink }) {
  const [projectLink, setProjectLink] = useState('');
  const [savedProjectLink, setSavedProjectLink] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const groupCode = String(group?.code || 'grupo').trim();
  const hasSavedProjectLink = isValidProjectLink(savedProjectLink);

  useEffect(() => {
    const initialProjectLink = getInitialProjectLink(group);
    setProjectLink(initialProjectLink);
    setSavedProjectLink(initialProjectLink);
  }, [group]);

  function handleClose() {
    if (isSaving) return;
    onClose?.();
  }

  async function handleSave() {
    const normalizedProjectLink = normalizeProjectLink(projectLink);

    if (!group?.id) {
      toast('No encontramos el grupo para guardar el link', {
        type: 'error'
      });
      return;
    }

    if (!normalizedProjectLink) {
      toast('Ingresa el link del proyecto final', {
        type: 'warning'
      });
      return;
    }

    if (normalizedProjectLink.length > MAX_PROJECT_LINK_LENGTH) {
      toast('El link debe tener maximo 255 caracteres', {
        type: 'warning'
      });
      return;
    }

    if (!isValidProjectLink(normalizedProjectLink)) {
      toast('Ingresa un link valido del proyecto', {
        type: 'warning'
      });
      return;
    }

    setIsSaving(true);
    const saveLink = saveProjectLink || updateGroupProjectLink;
    const savedGroup = await saveLink(group.id, normalizedProjectLink);
    setIsSaving(false);

    if (!savedGroup) {
      return;
    }

    const updatedGroup = {
      ...group,
      ...(typeof savedGroup === 'object' ? savedGroup : {}),
      description:
        typeof savedGroup === 'object' && savedGroup?.description
          ? savedGroup.description
          : normalizedProjectLink
    };
    setProjectLink(normalizedProjectLink);
    setSavedProjectLink(normalizedProjectLink);

    toast('Link de proyecto guardado exitosamente', {
      type: 'success'
    });
    onSaved?.(updatedGroup);
  }

  return (
    <section className='project-link-modal'>
      <h3 className='project-link-modal__title'>Link proyecto</h3>
      <p className='project-link-modal__text'>
        {'Aqu\u00ed puedes cargar el link de tu proyecto final con los grupos'}
      </p>
      <p className='project-link-modal__group'>Grupo: {groupCode || '-'}</p>
      {hasSavedProjectLink ? (
        <div className='project-link-modal__current-link'>
          <span className='project-link-modal__current-link-label'>
            Link actual
          </span>
          <a
            className='project-link-modal__visit-link'
            href={savedProjectLink}
            target='_blank'
            rel='noreferrer'
          >
            Ir al link
          </a>
        </div>
      ) : null}
      <label className='project-link-modal__label' htmlFor='project-link-input'>
        {hasSavedProjectLink ? 'Cambiar link del proyecto' : 'Link del proyecto'}
      </label>
      <input
        id='project-link-input'
        className='project-link-modal__input'
        type='url'
        value={projectLink}
        maxLength={MAX_PROJECT_LINK_LENGTH}
        placeholder='https://'
        onChange={event => setProjectLink(event.target.value)}
        disabled={isSaving}
      />
      <div className='project-link-modal__actions'>
        <button
          type='button'
          className='blue-site-button'
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving
            ? 'GUARDANDO...'
            : hasSavedProjectLink
              ? 'ACTUALIZAR LINK'
              : 'GUARDAR'}
        </button>
        <button
          type='button'
          className='button-white-border-blue'
          onClick={handleClose}
          disabled={isSaving}
        >
          CANCELAR
        </button>
      </div>
    </section>
  );
}

export default ProjectLinkModal;
